/**
 * useFileSelection Hook
 *
 * Centralized state management for multi-file selection across folder navigation.
 * Supports both multi-select (test files) and single-select (mapping file) modes.
 */

import { useState, useCallback } from 'react';
import type { SelectedExcelFile } from '../types/api';

export type { SelectedExcelFile };

export function useFileSelection(multiSelect: boolean = true) {
  const [selectedFiles, setSelectedFiles] = useState<Map<string, SelectedExcelFile>>(new Map());

  const addFile = useCallback((file: SelectedExcelFile) => {
    setSelectedFiles(prev => {
      const next = new Map(prev);
      if (!multiSelect) {
        // Single-select mode: replace existing selection
        next.clear();
      }
      next.set(file.id, file);
      return next;
    });
  }, [multiSelect]);

  const removeFile = useCallback((fileId: string) => {
    setSelectedFiles(prev => {
      const next = new Map(prev);
      next.delete(fileId);
      return next;
    });
  }, []);

  const toggleFile = useCallback((file: SelectedExcelFile) => {
    setSelectedFiles(prev => {
      const next = new Map(prev);
      if (next.has(file.id)) {
        next.delete(file.id);
      } else {
        if (!multiSelect) {
          // Single-select mode: replace existing selection
          next.clear();
        }
        next.set(file.id, file);
      }
      return next;
    });
  }, [multiSelect]);

  const addFiles = useCallback((files: SelectedExcelFile[]) => {
    setSelectedFiles(prev => {
      const next = new Map(prev);
      for (const file of files) {
        next.set(file.id, file);
      }
      return next;
    });
  }, []);

  const removeFiles = useCallback((fileIds: string[]) => {
    setSelectedFiles(prev => {
      const next = new Map(prev);
      for (const id of fileIds) {
        next.delete(id);
      }
      return next;
    });
  }, []);

  const clearAll = useCallback(() => {
    setSelectedFiles(new Map());
  }, []);

  const getFileList = useCallback(() => {
    return Array.from(selectedFiles.values());
  }, [selectedFiles]);

  const hasFile = useCallback((fileId: string) => {
    return selectedFiles.has(fileId);
  }, [selectedFiles]);

  return {
    selectedFiles,
    addFile,
    addFiles,
    removeFile,
    removeFiles,
    toggleFile,
    clearAll,
    getFileList,
    hasFile,
    count: selectedFiles.size,
  };
}
